import { useState } from "react";
import { Play, ArrowUpRight, Youtube } from "lucide-react";
import { VIDEOS, YOUTUBE_CHANNEL } from "../data/site";

export default function VideoKnowledgeCenter({ headingTag: Tag = "h3" }) {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="videos"
      data-testid="video-knowledge-center"
      className="section-pad"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline">Video Knowledge Center</div>
            <Tag className="font-serif-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Gastro &amp; Liver, explained on video.
            </Tag>
          </div>
          <a
            href={YOUTUBE_CHANNEL}
            target="_blank"
            rel="noreferrer"
            data-testid="video-channel-link"
            className="group inline-flex items-center gap-2 font-semibold text-[14px] border-b border-black pb-1 w-fit"
          >
            <Youtube className="h-4 w-4 text-[#FF0000]" />
            Visit the YouTube channel
            <ArrowUpRight className="h-4 w-4 arrow" />
          </a>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Featured */}
          <div
            data-testid="video-featured"
            className="lg:col-span-8 relative overflow-hidden rounded-[6px] border border-black/10 bg-black"
          >
            <div className="aspect-[16/10] relative">
              {playing ? (
                <iframe
                  title={VIDEOS.featured.title}
                  src={`https://www.youtube.com/embed/${VIDEOS.featured.id}?autoplay=1&rel=0`}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="group absolute inset-0 w-full h-full"
                  aria-label={`Play ${VIDEOS.featured.title}`}
                  data-testid="video-featured-play"
                >
                  <img
                    src={VIDEOS.featured.thumb}
                    alt={VIDEOS.featured.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.src = `https://img.youtube.com/vi/${VIDEOS.featured.id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-semibold">
                    Featured
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-16 w-16 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="h-6 w-6 ml-1 text-[#0370AD]" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-7 text-left text-white">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-white/70">
                      {VIDEOS.featured.duration} · From the channel
                    </div>
                    <h4 className="mt-3 font-display-semibold text-3xl md:text-[34px] leading-tight max-w-2xl">
                      {VIDEOS.featured.title}
                    </h4>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            {VIDEOS.list.map((v, i) => (
              <a
                key={v.id || i}
                href={v.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`video-list-${i}`}
                className="group bg-white border border-black/10 rounded-[6px] flex overflow-hidden hover:border-[#0370AD] transition-colors"
              >
                <div className="relative w-32 sm:w-40 shrink-0 overflow-hidden">
                  <img
                    src={v.thumb}
                    alt={v.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/15">
                    <Play className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-black/45 font-semibold">
                    {v.duration}
                  </div>
                  <h4 className="font-display-semibold text-[16px] leading-snug mt-1">
                    {v.title}
                  </h4>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="mt-10 flex flex-wrap gap-2.5">
          {VIDEOS.categories.map((c) => (
            <a
              key={c}
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noreferrer"
              data-testid={`video-category-${c.replace(/\s/g, "-").toLowerCase()}`}
              className="px-4 py-2 rounded-full border border-black/15 text-[12.5px] font-medium hover:bg-black hover:text-white transition-colors"
            >
              {c}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
